<script>
  import gql from 'graphql-tag';
  import { client } from 'utils/apollo.js';

  import { getContext } from 'svelte';

  import { productDataStore } from '../../stores/ProductData.js';
  import { webPSupport } from '../../stores/WebP.js';
  import { isMobile, designTabActive } from '../../stores/Designer.js';

  import LimitedTextArea from './LimitedTextArea.svelte';
  import Options from './Options.svelte';
  import ColoredButton from './ColoredButton.svelte';
  import AlignLeft from '../../svg/AlignLeft.svelte';
  import AlignCenter from '../../svg/AlignCenter.svelte';
  import AlignRight from '../../svg/AlignRight.svelte';
  import AlignJustified from '../../svg/AlignJustified.svelte';
  import FontOptions from './FontOptions.svelte';
  import FontColour from './FontColour.svelte';
  import FontShapeOptions from './FontOptions.svelte';
  import { fontShapeOptions } from '../../routes/design/[productSlug]/_fontShapes.js';
  import { previewImages } from '../../routes/design/[productSlug]/_previewImages.js';
  import PrintTypeSelection from './PrintTypeSelection.svelte';

  export let addToKonva;
  export let segment;
  export let updateInKonva;
  export let editingElement;

  let printOptions = $productDataStore[$productDataStore.showing].printOptions;

  // Background colour of selected product item - to show preview against
  let selectedProductColourMain = $productDataStore[$productDataStore.showing].selectedColour.ProductColoursMasterMain;

  let defaultPreviewImage;
  if (selectedProductColourMain.name === 'white') {
    defaultPreviewImage = previewImages.textBlack;
  } else {
    defaultPreviewImage = previewImages.textWhite;
  }

  // DEFAULTS
  let aligned = 'centred';
  let shape = fontShapeOptions[0];
  let spacing = 'single';
  let printOption = printOptions[0]?.PrintOption?.name || 'digital transfer';
  let previewImage = defaultPreviewImage;

  let font = {
    fontFamily: {
      name: 'freshman',
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M10.52 19.85v-.58h-.6v2.39h1.7V25H4.96v-3.34h.89v-8.13h-.89V10.2h10.88v3.92h-3.33v-.59H9.92v2.42h.6v-.59h3.32v4.49h-3.32zm10.007.36v2.13h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h7.33l1.89 1.9v3.04l-.79.78.79.76v2.7h.71V25h-3.96v-4.4l-.37-.39h-1.64zm0-4.39v1.73h1.64l.37-.39v-.95l-.37-.39h-1.64zm10.897 4.6v1.92h2.08v-.48h2.66V25h-8.7v-2.66h.71v-6.52h-.71v-2.66h8.7v3.14h-2.66v-.48h-2.08v1.94h.48v-.48h2.66v3.62h-2.66v-.48h-.48zm11.48.41-.37-.39h-3.46l-1.92-1.88v-3.5l1.9-1.9h5.2l1.9 1.9v2.34h-3.25v-1.19l-.39-.39h-1.72l-.39.39v1.19l.39.38h3.46l1.9 1.9v3.42l-1.9 1.9h-5.2l-1.9-1.9v-2.27h3.25v1.12l.39.39h1.72l.39-.39v-1.12zm9.803 1.51h.73v-1.97h-2.33v1.97h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h4.67v2.66h-.71v1.64h2.33v-1.64h-.73v-2.66h4.69v2.66h-.71v6.52h.71V25h-4.69v-2.66zm14.804-3.07-2.59 4.27-2.58-4.27v3.07h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.8l2.74 4.6 2.75-4.6h3.81v2.66h-.73v6.52h.73V25h-4.68v-2.66h.71v-3.07zm7.755-6.11h6.28v2.66h-.6l2.61 6.52h.71V25h-4.56v-2.66h.4l-.17-.55h-3.14l-.2.55h.39V25h-4.53v-2.66h.71l2.61-6.52h-.51v-2.66zm3.09 4.51-.77 1.85h1.53l-.76-1.85zM96.046 25h-3.9l-3.2-5.29v2.63h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.86l3.24 5.42v-2.76h-.71v-2.66h4.67v2.66h-.71v6.52h.71V25z" transform="translate(-.503 -2.6)"/></svg>`,
    },
    fill: 'White',
    rgb: '(255, 255, 255)',
    text: '',
  };

  // Have a flag for loading of $editingElement
  // Turn off editing if transformer turns off?
  let editingElementLoaded = false;
  $: if ($editingElement && !editingElementLoaded) {
    // set all variables to values from editing element
    font.text = $editingElement?.textArray ? JSON.parse($editingElement.textArray).join('\n') : ''; // leave one trailing newline for limited textarea this is a hack.
    previewImage = $editingElement.image;

    // Need to run function to get svg that matches name
    let fontElement = fonts.family.find((x) => x.name === $editingElement.font);
    font.fontFamily.name = fontElement.name;
    font.fontFamily.svg = fontElement.svg;

    font.rgb = $editingElement.textColour.rgb;
    font.fill = $editingElement.textColour.name;
    spacing = $editingElement.lineSpacing;
    aligned = $editingElement.textAlignment;
    shape = $editingElement.textShape;
    printOption = $editingElement.printOption || 'digital transfer';
    editingElementLoaded = true;
  }

  // When transformer is detached editingElement becomes null
  // Need to reset variables to defaults in this scenario
  $: if (!$editingElement && editingElementLoaded) {
    // set variables back to defaults
    aligned = 'centred';
    shape = fontShapeOptions[0];
    spacing = 'single';
    printOption = printOptions[0]?.PrintOption?.name || 'digital transfer';
    previewImage = defaultPreviewImage;

    font = {
      fontFamily: {
        name: 'freshman',
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M10.52 19.85v-.58h-.6v2.39h1.7V25H4.96v-3.34h.89v-8.13h-.89V10.2h10.88v3.92h-3.33v-.59H9.92v2.42h.6v-.59h3.32v4.49h-3.32zm10.007.36v2.13h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h7.33l1.89 1.9v3.04l-.79.78.79.76v2.7h.71V25h-3.96v-4.4l-.37-.39h-1.64zm0-4.39v1.73h1.64l.37-.39v-.95l-.37-.39h-1.64zm10.897 4.6v1.92h2.08v-.48h2.66V25h-8.7v-2.66h.71v-6.52h-.71v-2.66h8.7v3.14h-2.66v-.48h-2.08v1.94h.48v-.48h2.66v3.62h-2.66v-.48h-.48zm11.48.41-.37-.39h-3.46l-1.92-1.88v-3.5l1.9-1.9h5.2l1.9 1.9v2.34h-3.25v-1.19l-.39-.39h-1.72l-.39.39v1.19l.39.38h3.46l1.9 1.9v3.42l-1.9 1.9h-5.2l-1.9-1.9v-2.27h3.25v1.12l.39.39h1.72l.39-.39v-1.12zm9.803 1.51h.73v-1.97h-2.33v1.97h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h4.67v2.66h-.71v1.64h2.33v-1.64h-.73v-2.66h4.69v2.66h-.71v6.52h.71V25h-4.69v-2.66zm14.804-3.07-2.59 4.27-2.58-4.27v3.07h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.8l2.74 4.6 2.75-4.6h3.81v2.66h-.73v6.52h.73V25h-4.68v-2.66h.71v-3.07zm7.755-6.11h6.28v2.66h-.6l2.61 6.52h.71V25h-4.56v-2.66h.4l-.17-.55h-3.14l-.2.55h.39V25h-4.53v-2.66h.71l2.61-6.52h-.51v-2.66zm3.09 4.51-.77 1.85h1.53l-.76-1.85zM96.046 25h-3.9l-3.2-5.29v2.63h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.86l3.24 5.42v-2.76h-.71v-2.66h4.67v2.66h-.71v6.52h.71V25z" transform="translate(-.503 -2.6)"/></svg>`,
      },
      fill: 'White',
      rgb: '(255, 255, 255)',
      text: '',
    };
    // We've reset to default so mark editing loaded back to false so this block doesn't run again
    editingElementLoaded = false;
  }

  const designerFonts = getContext('designerFonts');
  const heatTransferPrintColours = getContext('heatTransferPrintColours');

  let fonts = {
    family: [],
    color: [],
    rgb: [],
  };

  fonts.family = designerFonts.map((item) => {
    return { name: item.name, svg: item.svg };
  });

  fonts.colour = heatTransferPrintColours.map((item) => {
    return { name: item.colour, rgb: item.colourRgb };
  });

  font.fill = fonts.colour[0].name;
  font.rgb = fonts.colour[0].rgb;

  function generateTextPreviewVariables(font, shape, aligned, spacing, textOutline = 'none') {
    let variables = {
      textArray: JSON.stringify(font.text.split('\n')),
      font: font.fontFamily.name,
      textColour: font.rgb,
      textShape: shape?.name || 'straight',
      textOutline: textOutline,
      textAlignment: aligned.toLowerCase(),
      lineSpacing: spacing.toLowerCase(),
      webp: $webPSupport,
    };
    return variables;
  }

  function setPreviewImage(image) {
    previewImage = image;
    // If editing exising element we can live update the item on the product
    if ($editingElement) {
      addText();
    }
  }

  // Make a reactive statement to update preview image src. Need to use .then pattern as img generation is async
  $: imagePromise = updateTextPreview(font, shape, aligned, spacing, printOption).then(setPreviewImage);

  async function updateTextPreview(font, shape, aligned, spacing, printOption) {
    if (typeof window !== 'undefined' && font.text) {
      let variables = generateTextPreviewVariables(font, shape, aligned, spacing);
      let image = await generateTextPreview(variables);
      return image;
    } else {
      return defaultPreviewImage;
    }
  }

  async function generateTextPreview(variables) {
    const IMAGE = gql`
      query MyQuery(
        $font: String!
        $lineSpacing: String!
        $textArray: jsonb!
        $textColour: String!
        $textOutline: String!
        $textShape: String!
        $webp: Boolean!
        $textAlignment: String!
      ) {
        textPreview(
          font: $font
          lineSpacing: $lineSpacing
          textArray: $textArray
          textColour: $textColour
          textOutline: $textOutline
          textShape: $textShape
          webp: $webp
          textAlignment: $textAlignment
        ) {
          image
        }
      }
    `;

    const res = await client.query({
      query: IMAGE,
      variables: variables,
    });

    const data = await res.data;
    return data.textPreview.image;
  }

  async function addText() {
    if (font.text) {
      let variables = generateTextPreviewVariables(font, shape, aligned, spacing);
      const image = await generateTextPreview(variables);
      let details = {
        printType: 'text',
        productType: $productDataStore.showing,
        segment: segment,
        image: image,
        textArray: JSON.stringify(font.text.split('\n')),
        font: font.fontFamily.name,
        textColour: {
          name: font.fill,
          rgb: font.rgb,
        },
        textShape: shape,
        textOutline: 'none',
        textAlignment: aligned.toLowerCase(),
        printOption: printOption,
        lineSpacing: spacing.toLowerCase(),
        webp: $webPSupport,
      };

      if ($editingElement) {
        updateInKonva($editingElement, image, details);
      } else {
        addToKonva(image, details);
        // reset variables to defaults - can i trigger this by resetting editing element to null?
        font.text = '';
        aligned = 'centred';
        shape = fontShapeOptions[0];
        spacing = 'single';
        printOption = printOptions[0]?.PrintOption?.name || 'digital transfer';
        previewImage = defaultPreviewImage;

        font = {
          fontFamily: {
            name: 'freshman',
            svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M10.52 19.85v-.58h-.6v2.39h1.7V25H4.96v-3.34h.89v-8.13h-.89V10.2h10.88v3.92h-3.33v-.59H9.92v2.42h.6v-.59h3.32v4.49h-3.32zm10.007.36v2.13h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h7.33l1.89 1.9v3.04l-.79.78.79.76v2.7h.71V25h-3.96v-4.4l-.37-.39h-1.64zm0-4.39v1.73h1.64l.37-.39v-.95l-.37-.39h-1.64zm10.897 4.6v1.92h2.08v-.48h2.66V25h-8.7v-2.66h.71v-6.52h-.71v-2.66h8.7v3.14h-2.66v-.48h-2.08v1.94h.48v-.48h2.66v3.62h-2.66v-.48h-.48zm11.48.41-.37-.39h-3.46l-1.92-1.88v-3.5l1.9-1.9h5.2l1.9 1.9v2.34h-3.25v-1.19l-.39-.39h-1.72l-.39.39v1.19l.39.38h3.46l1.9 1.9v3.42l-1.9 1.9h-5.2l-1.9-1.9v-2.27h3.25v1.12l.39.39h1.72l.39-.39v-1.12zm9.803 1.51h.73v-1.97h-2.33v1.97h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h4.67v2.66h-.71v1.64h2.33v-1.64h-.73v-2.66h4.69v2.66h-.71v6.52h.71V25h-4.69v-2.66zm14.804-3.07-2.59 4.27-2.58-4.27v3.07h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.8l2.74 4.6 2.75-4.6h3.81v2.66h-.73v6.52h.73V25h-4.68v-2.66h.71v-3.07zm7.755-6.11h6.28v2.66h-.6l2.61 6.52h.71V25h-4.56v-2.66h.4l-.17-.55h-3.14l-.2.55h.39V25h-4.53v-2.66h.71l2.61-6.52h-.51v-2.66zm3.09 4.51-.77 1.85h1.53l-.76-1.85zM96.046 25h-3.9l-3.2-5.29v2.63h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.86l3.24 5.42v-2.76h-.71v-2.66h4.67v2.66h-.71v6.52h.71V25z" transform="translate(-.503 -2.6)"/></svg>`,
          },
          fill: 'White',
          rgb: '(255, 255, 255)',
          text: '',
        };
      }
    }
  }
</script>

<section class="">
  <div class="preview">
    <img src={previewImage} alt="Text Preview" style="--bgColour: rgb{selectedProductColourMain.rgb}" />
  </div>
  <LimitedTextArea bind:text={font.text} rows={3} columns={16} />

  <div class="grid">
    <div>
      <div class="label">Font Family</div>
      <FontOptions bind:options={fonts.family} bind:selected={font.fontFamily} />
    </div>
    <div>
      <div class="label">Text Colour</div>
      <FontColour bind:options={fonts.colour} bind:selected={font.fill} bind:selectedRgb={font.rgb} />
    </div>
    <div>
      <span class="label">Font Shape </span>
      <FontShapeOptions options={fontShapeOptions} bind:selected={shape} />
    </div>

    <div>
      <span class="label">Line Spacing </span>
      <Options label="Spacing" options={['half', 'single', 'double']} bind:selected={spacing} />
    </div>
  </div>

  <div class="align-container">
    <span class="align-label">Align</span>
    <div class="align">
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class:active={aligned === 'left'}
        on:click={() => {
          aligned = 'left';
        }}
      >
        <AlignLeft />
      </div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class:active={aligned === 'centred'}
        on:click={() => {
          aligned = 'centred';
        }}
      >
        <AlignCenter />
      </div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class:active={aligned === 'right'}
        on:click={() => {
          aligned = 'right';
        }}
      >
        <AlignRight />
      </div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class:active={aligned === 'justified'}
        on:click={() => {
          aligned = 'justified';
        }}
      >
        <AlignJustified />
      </div>
    </div>
  </div>

  {#if printOptions.length > 1}
    <PrintTypeSelection {printOptions} bind:printOption />
  {/if}

  <div class="grid">
    {#if !$editingElement}
      <ColoredButton label="+ Add Text" type="green" clickFn={addText} />
    {/if}
    {#if $isMobile && $designTabActive}
      {#if $editingElement}
        <ColoredButton label="Update" type="green" clickFn={() => ($designTabActive = false)} />
      {/if}
      <ColoredButton label="Close" type="red" clickFn={() => ($designTabActive = false)} />
    {/if}
  </div>
</section>

<style>
  section {
    margin: 0;
    padding-top: 1rem;
    padding-bottom: 2.2rem;
  }

  .preview {
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .preview img {
    background-color: var(--bgColour);
    padding: 1rem;
    max-height: 90%;
  }

  .grid {
    margin-top: 1rem;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  .align-label,
  .label {
    font-weight: bold;
    color: #666;
    letter-spacing: 1.5px;
    font-size: 1.2rem;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
    display: block;
  }

  .align-container {
    margin: 2rem 0;
  }

  .align {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    background-color: #f5f5f7;
    border-radius: 1rem;
    color: #666;
  }

  .align > div {
    text-align: center;
    padding: 0.2rem 0;
    cursor: pointer;
  }

  .align .active {
    border: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 0 0 1rem rgba(0, 0, 0, 0.15);
    border-radius: 1rem;
    color: #000;
  }
</style>
